/**
 * AP-32 13.4 — **Textbausteine je Art und Sprache.** Einleitung, Schluss,
 * Steuerhinweise, Zahlungs- und Mahntexte, Mailtexte. Ein geleerter Baustein
 * ist kein leerer Text, sondern keiner — der Server entfernt ihn.
 *
 * AP-36 Phase D: ohne Knopf. Jeder Baustein ist eine eigene Stelle
 * (`textBlock:<art>:<sprache>:<anrede>:<platz>`) und speichert beim Verlassen,
 * geprüft gegen den Stand dieses Bausteins. „Für alle Arten übernehmen" bleibt
 * eine Handlung mit Rückfrage.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { DOCUMENT_KINDS, type DocumentKind } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { api } from '@/lib/axios'
import { FeldStatus, mitStand, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { pageWide } from '@/lib/form-styles'
import { documentsApi } from '../api/documents.api'
import { SettingsTabs } from '../components/SettingsTabs'
import type { DocumentSettingsData, TextBlockRow } from '../types/document.types'
import { bestaetige } from '@/lib/dialoge'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring aria-[invalid=true]:border-destructive'

/** Welche Plätze eine Art überhaupt braucht — der Rest wäre ein Feld ohne Wirkung. */
function plaetzeFuer(kind: DocumentKind, daten: DocumentSettingsData): string[] {
  const f = daten.kinds.find((k) => k.kind === kind)?.capabilities
  return daten.slots.filter((slot) => {
    if (slot.startsWith('TAX_NOTE_')) return f?.prices !== 'no'
    if (slot.startsWith('PAYMENT')) return !!f?.payable
    if (slot.startsWith('DUNNING_')) return kind === 'DUNNING'
    return true
  })
}

export default function DocumentTextsPage() {
  const { t } = useTranslation()
  const [daten, setDaten] = useState<DocumentSettingsData | null>(null)
  const [kind, setKind] = useState<DocumentKind>('OFFER')
  const [sprache, setSprache] = useState<'de' | 'en'>('de')
  /** Nachtrag 2: Sie/du — nur im Deutschen; der Ansprechpartner entscheidet, welche Fassung gilt. */
  const [anrede, setAnrede] = useState<'FORMAL' | 'INFORMAL'>('FORMAL')
  const form = sprache === 'de' ? anrede : 'FORMAL'
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  useEffect(() => {
    documentsApi
      .settings()
      .then((r) => setDaten(r.data.data))
      .catch((err) => setFehler(fehlertext(err, t)))
  }, [t])

  useEffect(() => setMeldung(null), [kind, sprache, form])

  if (!daten) {
    return <div className={pageWide}>{fehler ? <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div> : t('common.loading')}</div>
  }

  /**
   * N-16: Den Text eines Platzes in **alle** Arten schreiben, die ihn führen —
   * eine Kopie, keine Kopplung. Die Texte bleiben danach je Art änderbar.
   */
  const fuerAlleArten = async (slot: string, text: string) => {
    if (!(await bestaetige(t('documents.texts.applyAllConfirm')))) return
    setFehler(null)
    setMeldung(null)
    try {
      let letzte: DocumentSettingsData | null = null
      let anzahl = 0
      for (const k of DOCUMENT_KINDS) {
        if (!plaetzeFuer(k, daten).includes(slot)) continue
        letzte = (await documentsApi.updateTexts(k, [{ language: sprache, formOfAddress: form, slot, text }])).data.data
        anzahl++
      }
      if (letzte) setDaten(letzte)
      setMeldung(t('documents.texts.appliedAll', { count: anzahl }))
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }

  return (
    <div className={pageWide}>
      <div className="flex items-center gap-3">
        <Link to="/correspondence" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('documents.settings.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('documents.texts.subtitle')}</p>
        </div>
      </div>
      <SettingsTabs />

      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.settings.kind')}</span>
          <select className={eingabe} value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)}>
            {DOCUMENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`documents.kinds.${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.texts.language')}</span>
          <select className={eingabe} value={sprache} onChange={(e) => setSprache(e.target.value as 'de' | 'en')}>
            <option value="de">{t('documents.texts.languages.de')}</option>
            <option value="en">{t('documents.texts.languages.en')}</option>
          </select>
        </label>
        {sprache === 'de' && (
          <label className="space-y-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.texts.formOfAddress')}</span>
            <select className={eingabe} value={anrede} onChange={(e) => setAnrede(e.target.value as 'FORMAL' | 'INFORMAL')}>
              <option value="FORMAL">{t('documents.texts.formsOfAddress.FORMAL')}</option>
              <option value="INFORMAL">{t('documents.texts.formsOfAddress.INFORMAL')}</option>
            </select>
          </label>
        )}
        {meldung && <span className="text-sm text-emerald-600">✓ {meldung}</span>}
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      <p className="text-xs text-muted-foreground">{t('documents.texts.placeholders')}</p>

      <div className="grid gap-4 lg:grid-cols-2">
        {plaetzeFuer(kind, daten).map((slot) => (
          <Baustein
            key={`${kind}:${sprache}:${form}:${slot}`}
            kind={kind}
            sprache={sprache}
            form={form}
            slot={slot}
            block={daten.textBlocks.find((b) => b.kind === kind && b.language === sprache && (b.formOfAddress ?? 'FORMAL') === form && b.slot === slot)}
            onAlleArten={(text) => void fuerAlleArten(slot, text)}
          />
        ))}
      </div>
    </div>
  )
}

/** Ein Textbaustein — eine Stelle, gespeichert beim Verlassen. */
function Baustein({
  kind,
  sprache,
  form,
  slot,
  block,
  onAlleArten,
}: {
  kind: DocumentKind
  sprache: 'de' | 'en'
  form: 'FORMAL' | 'INFORMAL'
  slot: string
  block: TextBlockRow | undefined
  onAlleArten: (text: string) => void
}) {
  const { t } = useTranslation()
  const passt = (b: TextBlockRow) => b.kind === kind && b.language === sprache && (b.formOfAddress ?? 'FORMAL') === form && b.slot === slot
  /** Aus der Antwort (alle Einstellungen) oder dem Baustein selbst: Text und Stand. */
  const werte = (antwort: unknown): Datensatz => {
    const a = (antwort ?? {}) as Datensatz
    const innen = (a.data && typeof a.data === 'object' ? a.data : a) as Datensatz
    const b = Array.isArray(innen.textBlocks) ? (innen.textBlocks as TextBlockRow[]).find(passt) : (innen as unknown as TextBlockRow | undefined)
    return { text: b?.text ?? '', updatedAt: (b as Datensatz | undefined)?.updatedAt }
  }
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `textBlock:${kind}:${sprache}:${form}:${slot}`,
    bezeichnung: t(`documents.texts.slots.${slot}`),
    daten: (block ?? { text: '' }) as unknown as Datensatz,
    werte,
    schreibe: (patch, stand) =>
      api
        .put<Datensatz>(`/documents/settings/${kind}/texts`, { blocks: [{ language: sprache, formOfAddress: form, slot, text: (patch.text as string | null) ?? '' }] }, mitStand(stand))
        .then((r) => r.data),
  }) as Speicherstelle<unknown>
  const text = useFeld<string>(stelle, 'text', { label: t(`documents.texts.slots.${slot}`), lies: (x) => x, schreib: (w) => w ?? '' })

  return (
    <div className="space-y-1 rounded-lg border border-border bg-card p-3 shadow-sm">
      <label className="block space-y-1">
        <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`documents.texts.slots.${slot}`)}</span>
        <textarea rows={slot === 'EMAIL_BODY' ? 6 : 3} className={eingabe} {...text.props} />
      </label>
      <FeldStatus status={text} />
      <button type="button" onClick={() => onAlleArten(text.wert ?? '')} className="text-xs text-primary hover:underline">
        {t('documents.texts.applyAll')}
      </button>
    </div>
  )
}
